"use client";
import { Info, X } from "lucide-react";
import { useSignInModal } from "./sign-in-modal";
import { useGenerator } from "@/app/context/GeneratorContext";

interface Tag {
  id: string;
  name: string;
  selected: boolean;
  savedTag?: boolean;
}

interface Category {
  id: string;
  name: string;
  type: string;
  tags: Tag[];
}

interface GeneratorState {
  tagList: Category[];
}

export default function Toggle({
  toggle,
  id,
  toggleHide,
  editMode,
  togglesToHide,
  generationType,
  translations,
  signInModalTranslations,
}: {
  toggle: Tag;
  id: any;
  toggleHide(): any;
  editMode: boolean;
  togglesToHide: any[];
  generationType: string;
  translations: any;
  signInModalTranslations: any;
}) {
  const { state, tagList, gifTagList, setGifTagList, setTagList, tagListLoading } = useGenerator();
  const { SignInModal, setShowSignInModal } = useSignInModal(
    signInModalTranslations,
    // {
    //   signInModalSignIn: locale.signInModal.signIn,
    //   signInModalSignInDescription: locale.signInModal.signInDescription,
    //   signInModalSignInButton: locale.signInModal.signInButton,
    //   copy: locale.share.copy,
    //   copied: locale.share.copied,
    //   toolTip1: locale.create.toolTip1,
    //   toolTip2: locale.create.toolTip2,
    //   toolTip3: locale.create.toolTip3,
    // },
    toggle.id,
    toggle.name,
    state.selectedGenerator.shortName,
  );

  // Check if ugc or default
  const isUgc: boolean = toggle.savedTag !== undefined && toggle.savedTag;

  // Check if the toggle is selected
  const isSelected: boolean =
    generationType === "image"
      ? isToggleSelected(toggle.id, tagList)
      : isToggleSelected(toggle.id, gifTagList);

  // Function to determine if a toggle is selected
  function isToggleSelected(toggleId: string, tagList: Category[]): boolean {
    return tagList.some((category) =>
      category.tags.some((tag) => tag.id === toggleId && tag.selected),
    );
  }

  const handleToggleClick = () => {
    // Count the total number of selected tags
    let tagListClone = tagList;
    if (generationType === "gif") {
      tagListClone = gifTagList;
    }
    let totalSelected = 0;

    for (const tagGroup of tagListClone) {
      for (const tag of tagGroup.tags) {
        if (tag.selected) {
          totalSelected += 1;
        }
      }
    }

    // // // Check if the toggle is already selected or if selecting a new one exceeds the limit
    // if (!isSelected && totalSelected > state.creditCost.freeTagLimit) {
    //   //if (!isSelected && totalSelected >= 6 && !(user._id !== "none" && user.membership?.level === 1)) {
    //   // Trigger premium feature prompt
    //   // Example: setShowPremiumModal(true);
    //   // let creditSum = IS_MAIN_SITE ? user.credits.ccBill + user.credits.subStar + user.credits.daily : user.credits.subStar + user.credits.daily;
    //   // if (creditSum < (totalSelected) * state.creditCost.tagCost) {
    //   //   window.alert(locale.accessTagRestrictions);
    //   //   return; // Exit without changing state
    //   // }
    //   if (!hasEnoughCredits(user, state)) {
    //     window.alert(locale.accessTagRestrictions);
    //     return;
    //   }
    // }

    const newTagList: Category[] = tagListClone.map((category) => {
      if (category.id === id) {
        // Check if the category id matches the provided id prop
        const updatedTags = category.tags.map((tag) => {
          if (category.type === "single") {
            // Handle categories of type 'single'
            if (tag.id === toggle.id && !tag.selected) {
              return {
                ...tag,
                selected: true,
              };
            } else if (tag.id === toggle.id && tag.selected) {
              return {
                ...tag,
                selected: false,
              };
            } else {
              return {
                ...tag,
                selected: false,
              };
            }
          } else {
            // Handle other categories
            return {
              ...tag,
              selected: tag.id === toggle.id ? !tag.selected : tag.selected,
            };
          }
        });

        return {
          ...category,
          tags: updatedTags,
        };
      } else if (category.type === "single" && category.id === id) {
        // Unselect tags in other "single" categories
        const updatedTags = category.tags.map((tag) => ({
          ...tag,
          selected: false,
        }));

        return {
          ...category,
          tags: updatedTags,
        };
      }

      return category;
    });

    if (generationType === "image") {
      setTagList(newTagList);
    } else if (generationType === "gif") {
      setGifTagList(newTagList);
    }
  };

  const isHidden = togglesToHide.includes(toggle.id);

  return (
    <>
      <SignInModal />
      <button
        type="button"
        onClick={editMode ? toggleHide : handleToggleClick}
        style={{
          backgroundColor: isSelected && !tagListLoading ? "#0772DD" : "#343639",
          opacity: isHidden ? 0.5 : 1, // Set opacity to 50% if hidden
          border: tagListLoading ? "0px" : isUgc ? "0.5px solid #BF40BF" : "0.5px solid #3C3C3C",
        }}
        className={`mb-2 mr-2 rounded-full border-[0.5px] border-[#3C3C3C] pl-3.5 pr-0 text-sm font-medium text-white hover:bg-gray-900 hover:brightness-105 focus:outline-none focus:ring-0 focus:ring-gray-300 sm:pl-5 sm:pr-0 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700 dark:focus:ring-gray-700 ${
          tagListLoading && "animate-pulse"
        }`}
      >
        <div className={`flex flex-row ${tagListLoading ? "opacity-0" : "opacity-100"}`}>
          <div className="my-auto">{toggle.name}</div>
          <div className="border-1 my-auto ml-[8px] h-3 border-r border-white"></div>
          {generationType != "gif" ? (
            <>
              {editMode ? (
                <div
                  aria-label="Hide This Tag"
                  className="w-auto py-[4px] pl-[8px] pr-3 sm:py-1.5"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleHide(); // Call toggleHide when clicked
                  }}
                >
                  <X color="white" className="my-auto h-5 w-auto" /> {/* X icon */}
                </div>
              ) : (
                <div
                  aria-label="Top Posts Using This Tag"
                  className="w-auto py-[4px] pl-[8px] pr-3 sm:py-1.5"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowSignInModal(true);
                  }}
                >
                  <Info color="white" className="my-auto h-5 w-auto" /> {/* Info icon */}
                </div>
              )}
            </>
          ) : (
            <>
              {" "}
              <div
                aria-label="Top Posts Using This Tag"
                className="w-auto py-[4px] pl-[8px] pr-3 sm:py-1.5"
              >
                <Info color="white" className="my-auto h-5 w-auto" /> {/* Info icon */}
              </div>
            </>
          )}
        </div>
      </button>
    </>
  );
}
